import { FC, useEffect, useState } from "react"
import { observer } from "mobx-react"
import { useStore } from "stores/utils/use-store"
import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  Checkbox,
  Descriptions,
  Input,
  InputNumber,
  Modal,
  Progress,
  Space,
  Table,
  Tag,
  Typography,
} from "antd"
import { Link, useNavigate, useParams } from "react-router-dom"
import { DeliverySelectionStatus, SelectionReviewVerdict, SelectionSession } from "../store/types"
import { SELECTION_PAGE_SIZE } from "../store/data-delivery.store"
import { fmtDuration } from "../utils"
import { StatsSummary } from "./selection-planner"

const SELECTION_COLOR: Record<DeliverySelectionStatus, string> = {
  PENDING: "default",
  PLANNING: "processing",
  DRAFT: "blue",
  REVIEWING: "gold",
  APPROVED: "cyan",
  EXECUTED: "green",
  DISCARDED: "default",
  FAILED: "red",
}

// Planning runs off the request path, so poll until it settles.
const IN_FLIGHT: DeliverySelectionStatus[] = ["PENDING", "PLANNING"]

const VERDICT_COLOR: Record<SelectionReviewVerdict, string> = {
  PENDING: "default",
  LEGIT: "green",
  REJECTED: "red",
}

export const SelectionReview: FC = observer(() => {
  const { dataStore: { dataDeliveryStore: store } } = useStore()
  const { id: selectionId = "" } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [sampledOnly, setSampledOnly] = useState(true)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(SELECTION_PAGE_SIZE)
  const [sampleOpen, setSampleOpen] = useState(false)
  const [sampleSize, setSampleSize] = useState(25)
  const [executeOpen, setExecuteOpen] = useState(false)
  const [chunkSize, setChunkSize] = useState(500)
  const [includeInputStreams, setIncludeInputStreams] = useState(true)
  const [includeEventLogs, setIncludeEventLogs] = useState(false)
  const [includeDeviceMetadata, setIncludeDeviceMetadata] = useState(false)
  const [skipReviewGate, setSkipReviewGate] = useState(false)
  const [busy, setBusy] = useState(false)
  const [noteFor, setNoteFor] = useState<SelectionSession | null>(null)
  const [note, setNote] = useState("")

  useEffect(() => {
    // The route param is defaulted to "" — firing a request for it would only
    // produce an error toast.
    if (!selectionId) return
    store.loadSelection(selectionId, sampledOnly, page, pageSize)
  }, [store, selectionId, sampledOnly, page, pageSize])

  // Only the selection the URL asks for: `selectionDetail` still holds the
  // previous one until the new load resolves, and rendering that would show the
  // old rows and review gate while the action buttons target the new id.
  const detail = store.selectionDetail?.selection?.id === selectionId ? store.selectionDetail : null
  const selection = detail?.selection
  const status = selection?.status

  useEffect(() => {
    if (!selectionId || !status || !IN_FLIGHT.includes(status)) return undefined
    const t = setInterval(() => store.loadSelection(selectionId, sampledOnly, page, pageSize), 3000)

    return () => clearInterval(t)
  }, [store, selectionId, sampledOnly, page, pageSize, status])

  if (!selection) {
    return <Card loading={store.isLoading} title="Selection" />
  }

  const review = detail?.review ?? { sampled: 0, pending: 0, legit: 0, rejected: 0 }
  const sessions = detail?.sessions ?? []
  const missingItems = selection.missingDataItemCount
  const inFlight = IN_FLIGHT.includes(selection.status)
  // Only a planned selection can be sampled, reviewed or executed.
  const isOpen = selection.status === "DRAFT"
    || selection.status === "REVIEWING"
    || selection.status === "APPROVED"

  const openExecute = () => {
    setSkipReviewGate(false)
    setExecuteOpen(true)
  }

  const setVerdict = async(session: SelectionSession, verdict: SelectionReviewVerdict) => {
    setBusy(true)
    await store.reviewSession(selectionId, session.sessionId, verdict)
    // Reload the page being reviewed, not page 1.
    await store.reloadSelectionPage(selectionId, sampledOnly, page, pageSize)
    setBusy(false)
  }

  // The override is only meaningful while a gate is actually standing. Kept as
  // a derived value rather than trusted state: the checkbox can be ticked, the
  // dialog cancelled, a sample drawn and reviewed, and the stale `true` would
  // then ship a silent gate bypass with no warning on screen.
  const gateStanding = selection.sampleSize === null || review.pending > 0

  const onExecute = async() => {
    setBusy(true)
    const res = await store.executeSelection(selectionId, {
      chunkSize,
      includeInputStreams,
      includeEventLogs,
      includeDeviceMetadata,
      skipReviewGate: gateStanding && skipReviewGate,
    })
    setBusy(false)
    if (res) {
      setExecuteOpen(false)
      // One selection becomes N batches; jump to the first so the operator lands
      // somewhere useful, and the rest are listed on the contract.
      if (res.batchIds[0]) navigate(`/data-delivery/deliveries/${res.batchIds[0]}`)
    }
  }

  const columns = [
    {
      title: "Session",
      dataIndex: "sessionId",
      key: "sessionId",
      render: (v: string, s: SelectionSession) => (
        <Space size={4}>
          {s.sampled && <Tag color="blue">SAMPLE</Tag>}
          <span style={{ fontFamily: "monospace", fontSize: 12 }}>{v.slice(0, 8)}…</span>
          <Typography.Text copyable={{ text: v }} />
        </Space>
      ),
    },
    {
      title: "Game",
      key: "game",
      render: (_: unknown, s: SelectionSession) => `${s.gameId} · ${s.gameName ?? "—"}`,
    },
    { title: "Tier", dataIndex: "tier", key: "tier" },
    { title: "Players", dataIndex: "playerCount", key: "playerCount" },
    {
      title: "Length",
      key: "duration",
      render: (_: unknown, s: SelectionSession) => fmtDuration(Math.round(s.durationMs / 1000)),
    },
    { title: "Player-hours", key: "hours", render: (_: unknown, s: SelectionSession) => `${s.playerHours.toFixed(2)} h` },
    {
      title: "Start (UTC)",
      key: "start",
      render: (_: unknown, s: SelectionSession) => new Date(Number(s.utcStartMs)).toISOString().replace("T", " ").slice(0, 16),
    },
    { title: "Pipeline", dataIndex: "pipelineVersion", key: "pipelineVersion" },
    {
      title: "Watch",
      key: "watch",
      render: (_: unknown, s: SelectionSession) => s.replayUrl
        ? <a href={s.replayUrl} target="_blank" rel="noreferrer">▶ All players</a>
        : <Typography.Text type="secondary">no source prefix</Typography.Text>,
    },
    {
      title: "Verdict",
      key: "verdict",
      render: (_: unknown, s: SelectionSession) => (
        <Space size={4}>
          <Tag color={VERDICT_COLOR[s.verdict]}>{s.verdict}</Tag>
          {s.reviewNote && <Typography.Text type="secondary" style={{ fontSize: 12 }}>{s.reviewNote}</Typography.Text>}
        </Space>
      ),
    },
    {
      title: "",
      key: "actions",
      render: (_: unknown, s: SelectionSession) => {
        if (!isOpen) return null

        return (
          <Space size={4}>
            <Button size="small" type="text" disabled={busy} onClick={() => setVerdict(s, "LEGIT")}>Legit</Button>
            <Button size="small" type="text" danger disabled={busy} onClick={() => setVerdict(s, "REJECTED")}>Reject</Button>
            <Button
              size="small"
              type="text"
              onClick={() => {
                setNoteFor(s)
                setNote(s.reviewNote ?? "")
              }}
            >
              Note
            </Button>
          </Space>
        )
      },
    },
  ]

  return (
    <Space direction="vertical" style={{ width: "100%" }} size={16}>
      <Breadcrumb>
        <Breadcrumb.Item>
          <Link to="/data-delivery/contracts">Contracts</Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <Link to={`/data-delivery/contracts/${selection.contractId}`}>
            Contract {selection.contractId.slice(0, 8)}…
          </Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>Selection {selection.id.slice(0, 8)}…</Breadcrumb.Item>
      </Breadcrumb>

      <Card
        title={
          <Space>
            <span>{selection.name}</span>
            <Tag color={SELECTION_COLOR[selection.status]}>{selection.status}</Tag>
          </Space>
        }
        extra={
          <Space>
            {isOpen && (
              <Button size="small" onClick={() => setSampleOpen(true)}>
                {selection.sampleSize ? "Re-draw sample" : "Draw review sample"}
              </Button>
            )}
            {isOpen && (
              <Button size="small" type="primary" onClick={openExecute}>Execute</Button>
            )}
            {isOpen && (
              <Button size="small" danger onClick={() => store.discardSelection(selectionId)}>Discard</Button>
            )}
          </Space>
        }
      >
        <Space direction="vertical" style={{ width: "100%" }} size={12}>
          {inFlight && (
            <Alert
              type="info"
              showIcon
              message={selection.status === "PENDING" ? "Queued for planning" : "Planning in progress"}
              description={
                "The selection scan runs in the background because it does not fit a request — it reads every candidate "
                + "session, resolves the recordings against game-service, and writes a row per selected session. "
                + "This page refreshes itself."
              }
            />
          )}

          {selection.status === "FAILED" && (
            <Alert
              type="error"
              showIcon
              message="Planning failed"
              description={selection.failureReason ?? "No reason recorded."}
            />
          )}

          {selection.status === "EXECUTED" && (
            <Alert
              type="success"
              showIcon
              message={`Executed into ${selection.batchCount} batch(es)`}
              description={
                <Space wrap size={4}>
                  {selection.batchIds.map(id => (
                    <Link key={id} to={`/data-delivery/deliveries/${id}`}>{id.slice(0, 8)}…</Link>
                  ))}
                  {selection.batchCount > selection.batchIds.length && (
                    <span>+{selection.batchCount - selection.batchIds.length} more</span>
                  )}
                </Space>
              }
            />
          )}

          {missingItems > 0 && (
            <Alert
              type="warning"
              showIcon
              message={`${missingItems} recording(s) have no data item`}
              description={
                "A delivery reserves data items, so any session containing one of these is dropped at execute. "
                + "Sync data items for these recordings first."
              }
            />
          )}

          {review.sampled > 0 && (
            <Card size="small" title="Manual review">
              <Space size="large" align="center">
                <Progress
                  type="circle"
                  width={64}
                  percent={Math.round(((review.legit + review.rejected) / review.sampled) * 100)}
                />
                <Descriptions column={4} size="small">
                  <Descriptions.Item label="Sampled">{review.sampled}</Descriptions.Item>
                  <Descriptions.Item label="Pending">{review.pending}</Descriptions.Item>
                  <Descriptions.Item label="Legit">{review.legit}</Descriptions.Item>
                  <Descriptions.Item label="Rejected">{review.rejected}</Descriptions.Item>
                </Descriptions>
              </Space>
              <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8, marginBottom: 0 }}>
                The sample is drawn deterministically and spread across games, so reloading shows the same rows and one title
                cannot swallow the whole sample. Rejected sessions are excluded at execute; unsampled sessions are accepted.
              </Typography.Paragraph>
            </Card>
          )}

          {selection.stats && <StatsSummary stats={selection.stats} />}
        </Space>
      </Card>

      <Card
        size="small"
        title={`Sessions · ${detail?.total ?? 0}`}
        extra={
          <Checkbox
            checked={sampledOnly}
            onChange={e => {
              setSampledOnly(e.target.checked)
              setPage(1)
            }}
          >
            Review queue only
          </Checkbox>
        }
      >
        {/* Server-side: a selection routinely holds more sessions than one
            page, and slicing a single fetched page would silently hide the
            rest of the review queue. */}
        <Table
          rowKey="sessionId"
          size="small"
          columns={columns}
          dataSource={sessions}
          loading={store.isLoading}
          pagination={{
            current: page,
            pageSize,
            total: detail?.total ?? 0,
            showSizeChanger: true,
            showTotal: total => `${total} sessions`,
            onChange: (nextPage, nextSize) => {
              setPage(nextPage)
              if (nextSize && nextSize !== pageSize) setPageSize(nextSize)
            },
          }}
        />
      </Card>

      <Modal
        title="Draw review sample"
        open={sampleOpen}
        okText="Draw"
        onCancel={() => setSampleOpen(false)}
        onOk={async() => {
          setBusy(true)
          await store.sampleSelection(selectionId, sampleSize)
          setBusy(false)
          setSampleOpen(false)
          setSampledOnly(true)
          setPage(1)
        }}
        confirmLoading={busy}
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <span>Sessions to draw for manual check-up</span>
          <InputNumber min={1} value={sampleSize} onChange={v => setSampleSize(v ?? 25)} style={{ width: 160 }} />
          {!!selection.sampleSize && (
            <Alert
              type="warning"
              showIcon
              message="Re-drawing clears the existing verdicts"
              description="A verdict that survived a re-draw would claim a reviewer approved a session they were never shown."
            />
          )}
        </Space>
      </Modal>

      <Modal
        title="Execute selection"
        open={executeOpen}
        okText="Reserve and build"
        confirmLoading={busy}
        onCancel={() => setExecuteOpen(false)}
        onOk={onExecute}
      >
        <Space direction="vertical" style={{ width: "100%" }} size={8}>
          <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
            This reserves the accepted sessions against the contract quota and starts the build. Rejected sessions, and
            sessions with a member that has no data item, are dropped whole.
          </Typography.Paragraph>
          <Space>
            <span>Data items per batch</span>
            <InputNumber min={1} max={1000} value={chunkSize} onChange={v => setChunkSize(v ?? 500)} />
          </Space>
          <Checkbox checked={includeInputStreams} onChange={e => setIncludeInputStreams(e.target.checked)}>
            Include input streams (*.input.ndjson)
          </Checkbox>
          <Checkbox checked={includeEventLogs} onChange={e => setIncludeEventLogs(e.target.checked)}>
            Include GEP event logs (*.events.*)
          </Checkbox>
          <Checkbox checked={includeDeviceMetadata} onChange={e => setIncludeDeviceMetadata(e.target.checked)}>
            Include device_metadata.json
          </Checkbox>
          {selection.sampleSize === null && (
            <Alert
              type="warning"
              showIcon
              message="No review sample has been drawn"
              description={
                <Space direction="vertical" size={4}>
                  <span>Draw a sample first, or override to deliver with no manual check-up at all.</span>
                  <Checkbox checked={skipReviewGate} onChange={e => setSkipReviewGate(e.target.checked)}>
                    Execute without a review sample
                  </Checkbox>
                </Space>
              }
            />
          )}
          {review.pending > 0 && (
            <Alert
              type="warning"
              showIcon
              message={`${review.pending} sampled session(s) still unreviewed`}
              description={
                <Checkbox checked={skipReviewGate} onChange={e => setSkipReviewGate(e.target.checked)}>
                  Execute anyway
                </Checkbox>
              }
            />
          )}
        </Space>
      </Modal>

      <Modal
        title={noteFor ? `Note on session ${noteFor.sessionId.slice(0, 8)}…` : "Note"}
        open={!!noteFor}
        okText="Save note"
        onCancel={() => setNoteFor(null)}
        onOk={async() => {
          if (noteFor) {
            setBusy(true)
            await store.reviewSession(selectionId, noteFor.sessionId, noteFor.verdict, note)
            await store.reloadSelectionPage(selectionId, sampledOnly, page, pageSize)
            setBusy(false)
          }
          setNoteFor(null)
        }}
        confirmLoading={busy}
      >
        <Input.TextArea rows={3} value={note} onChange={e => setNote(e.target.value)} placeholder="What did you see?" />
      </Modal>
    </Space>
  )
})
